import { formatDistance } from 'date-fns';
import { ReactNode, useState } from 'react';

import Image from '@cartwhl/ui/atoms/Image';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';
import UncheckedIcon from '@cartwhl/ui/icons/UncheckedIcon';

export default function ProjectCard({
  title,
  href,
  type,
  preview,
  lastUpdatedAt,
  overlays,
  isSelected,
  showCheckedDuringDeselect,
  onCheckboxClick,
  onClick,
  isMobile,
}: {
  title: ReactNode;
  href: string;
  type: 'pose' | 'animate';
  preview: string;
  lastUpdatedAt: string;
  overlays?: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  isSelected?: boolean;
  showCheckedDuringDeselect?: boolean;
  onCheckboxClick?: (e: React.MouseEvent) => void;
  isMobile?: boolean;
}) {
  const [previewSrc, setPreviewSrc] = useState(preview);

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button[data-project-card-checkbox]')) {
      return;
    }
    if (
      target.closest('button') &&
      !target.closest('button[data-project-card-checkbox]')
    ) {
      return;
    }
    if (
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[role="button"]')
    ) {
      return;
    }

    if (onClick) {
      onClick(e);
    } else {
      window.location.href = href;
    }
  };

  return (
    <div
      data-project-card
      onClick={handleClick}
      className={`group relative flex cursor-pointer select-none flex-col gap-4 rounded-2xl p-2 transition-colors ${
        isSelected
          ? 'bg-blue/10 dark:bg-blue/20'
          : 'hover:bg-gray-15 dark:hover:bg-gray-75'
      }`}
    >
      <div className="relative aspect-video overflow-hidden rounded-xl">
        {onCheckboxClick && (
          <button
            data-project-card-checkbox
            onClick={(e) => {
              e.stopPropagation();
              onCheckboxClick(e);
            }}
            className={`absolute left-2 top-2 z-30 transition-opacity ${
              isSelected
                ? 'opacity-100'
                : isMobile
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            {isSelected || showCheckedDuringDeselect ? (
              <div className="relative flex h-8 w-8 items-center justify-center">
                <UncheckedIcon className="stroke-blue [&>rect]:fill-blue absolute inset-0 h-8 w-8 [&>rect]:[rx:64] [&>rect]:[ry:64]" />
                <CheckmarkIcon className="relative h-5 w-5 text-white" />
              </div>
            ) : (
              <UncheckedIcon className="stroke-gray-45 dark:stroke-gray-30 dark:[&>rect]:fill-gray-75 h-8 w-8 [&>rect]:fill-white [&>rect]:[rx:64] [&>rect]:[ry:64]" />
            )}
          </button>
        )}
        <div className="hover:shadow-menu block h-full w-full select-none">
          {!previewSrc ? (
            <div className="bg-gray-30 dark:bg-gray-70 flex h-full w-full items-center justify-center">
              <PersonIcon className="text-gray-70 dark:text-gray-45 h-16 w-16" />
            </div>
          ) : previewSrc.includes('thumbnail.mp4') ? (
            <video
              src={previewSrc}
              className="h-full w-full bg-gray-100 object-cover"
              muted
              loop
              autoPlay
              playsInline
              crossOrigin="anonymous"
              onError={() => {
                // If no project thumbnail exists, show empty state
                setPreviewSrc('');
              }}
            />
          ) : (
            <Image
              src={previewSrc}
              alt=""
              crossOrigin="anonymous"
              className="h-full w-full bg-gray-100 object-cover"
              onError={() => setPreviewSrc('')}
            />
          )}
        </div>
        {overlays}
      </div>
      <div className="flex select-none gap-3 px-2">
        <div
          className={`h-[14px] w-[14px] flex-shrink-0 rounded-full ${
            type === 'pose' ? 'bg-violet' : 'bg-green'
          }`}
        ></div>
        <div className="flex min-w-0 flex-1 flex-col">
          {typeof title === 'string' ? (
            <Text variant="studio/body/sm" className="select-none truncate">
              {title}
            </Text>
          ) : (
            <div className="min-w-0">{title}</div>
          )}
          <Text
            variant="studio/body/sm"
            className="text-secondary select-none pt-1"
          >
            Edited{' '}
            {formatDistance(lastUpdatedAt, new Date(), {
              includeSeconds: false,
              addSuffix: true,
            })}
          </Text>
        </div>
      </div>
    </div>
  );
}
